<template>
    <v-container fluid class="pa-0">
        <v-overlay :value="isLoading" z-index="1000">
            <v-progress-circular :size="70" :width="7" indeterminate></v-progress-circular>
        </v-overlay>
        <!-- ============================================= -->
        <l-map v-if="dispMap" :zoomControl="false" ref="map" :style="mapCSS()" :zoom="zoom" :minZoom="6" :center="[center.lat, center.lng]" @baselayerchange="baselayerChanged" @update:zoom="zoomUpdated" @update:center="centerUpdated" :options="mapOptions">
            <l-tile-layer v-for="provider in tileProviders" :key="provider.id" :url="provider.url" :visible="provider.visible" :options="provider.options" :name="provider.name" :attribution="provider.attribution" :opacity="0.9" :z-index="30" layer-type="base"></l-tile-layer>
            <l-geo-json :geojson="geojson" :options="geoOptions"></l-geo-json>
            <l-wms-tile-layer v-for="provider in nowCastProviders" :key="provider.id" :base-url="provider.url" :visible="provider.visible" :opacity="0.9" :z-index="100" layer-type="base2" :options="nowCastOptions"></l-wms-tile-layer>
            <l-control-layers position="topright" hideSingleBase></l-control-layers>
            <!-- ================ -->
            <l-control position="topleft">
                <div class="ma-3">
                    <v-btn @click="nowCastSW()" :color="nowCastVisible ? 'info' : undefined">
                        <v-icon>mdi-weather-pouring</v-icon>
                    </v-btn>
                </div>
                <div class="ma-3">
                    <v-btn @click="geoSW()" :color="geoJsonVisible ? 'info' : undefined">
                        <v-icon>mdi-waves-arrow-up</v-icon>
                    </v-btn>
                </div>
                <div class="ma-3">
                    <v-btn @click.stop="legendDlg = !legendDlg">凡例</v-btn>
                </div>
            </l-control>
            <!-- ================ -->
            <l-control position="bottomright">
                <div>{{ nowZoom }}</div>
            </l-control>
            <!-- ===== スライダー ===== -->
            <l-control position="bottomleft" v-show="nowCastVisible">
                <div class="mx-9" style="width: 70vw">
                    <v-slider v-model="sliderVal" min="0" :max="sliderMax" @input="nowCastChange()" hide-details color="green darken-1" track-color="green lighten-2" thumb-label="always" thumb-size="50">
                        <template v-slot:thumb-label>
                            <h3>{{ nowCastTime }}</h3>
                        </template>
                    </v-slider>
                </div>
            </l-control>
            <!-- ================ -->
            <l-marker v-for="(p, idx) in markers" :key="idx" :lat-lng="[p.lat, p.lng]" :icon="p.icon">
                <l-popup>
                    <h2>{{ p.device_name }}</h2>
                    <h4 class="my-2">{{ p.device.gps }}</h4>
                    <v-btn class="ma-1" :to="{ name: 'DeviceChart', params: { device_id: p.device_id } }" color="success">
                        <v-icon>mdi-chart-line</v-icon>
                    </v-btn>
                    <!--
                    <v-btn class="ma-1" :to="{ name: 'DeviceImage', params: { device_id: p.device_id } }" color="yellow">
                        <v-icon>mdi-camera-outline</v-icon>
                    </v-btn>
                    -->
                    <v-btn class="ma-1" :to="{ name: 'Playback', params: { device_id: p.device.id } }" color="success">
                        <v-icon>mdi-video-outline</v-icon>
                    </v-btn>
                    <h5 class="text-right">{{ $moment(p.device.alived_at).format("YYYY-MM-DD HH:mm") }}</h5>
                </l-popup>
            </l-marker>
            <!-- ================ -->
            <v-dialog v-model="legendDlg" style="z-index: 1000" width="350">
                <v-card color="yellow lighten-5">
                    <v-toolbar color="info" dark>
                        <v-toolbar-title><h3>凡例</h3></v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon dark @click="legendDlg = false"><v-icon>mdi-close</v-icon></v-btn>
                    </v-toolbar>
                    <v-card-text>
                        <div class="d-flex align-center ma-5">
                            <img :src="legendMarker" class="marker-blue" />
                            <h3 class="ml-5">通常水位時</h3>
                        </div>
                        <div class="d-flex align-center ma-5">
                            <img :src="legendMarker" class="marker-red" />
                            <h3 class="ml-5">注意/危険/変動水位観測時</h3>
                        </div>
                        <div class="d-flex align-center ma-5">
                            <img :src="legendMarker" class="marker-gray" />
                            <h3 class="ml-5">休止・通信異常</h3>
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <!-- ================ -->
        </l-map>
    </v-container>
</template>

<style>
.marker-gray {
    filter: brightness(1.1) grayscale(90%);
}
.marker-blue {
    filter: hue-rotate(0deg);
}
.marker-purple {
    filter: hue-rotate(50deg);
}
.marker-pink {
    filter: hue-rotate(100deg);
}
.marker-red {
    filter: hue-rotate(150deg);
}
.marker-brown {
    filter: hue-rotate(180deg);
}
.marker-yellow {
    filter: brightness(1.8) hue-rotate(220deg);
}
.marker-green {
    filter: hue-rotate(270deg);
}
</style>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LControl, LControlLayers, LWMSTileLayer, LGeoJson } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
//import "leaflet-sprite";

import L from "leaflet";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    markerColor: "orange",
    //imagePath: "leaflet/dist/images",
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
L.Icon.Default.imagePath = "img";
//--- 色マーカー ---
import "leaflet-sprite/dist/leaflet.sprite.js";
//------------------------------------------
// import geoData from "@/assets/merge-geojson.json";
//------------------------------------------
export default {
    name: "DevicesMap",
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LControl,
        LControlLayers,
        LGeoJson,
        "l-wms-tile-layer": LWMSTileLayer,
    },
    //========================================================
    data() {
        return {
            jwt: "",
            myInfo: [],
            dbSites: [],
            dbDevices: [],
            dbLastSensings: [],
            //----------------------------------------------
            legendDlg: false,
            legendMarker: null,
            //----------------------------------------------
            dispMap: false,
            mapOptions: {
                zoomControl: false,
            },
            //----------------------------------------------
            geojson: null, //geoData, //require("@/assets/N03-20240101_24.geojson"),
            geoJsonVisible: false,
            geoOptions: {
                color: "blue",
                weight: 1.5,
                opacity: 1,
                fillColor: "blue",
                fillOpacity: 0.2,
            },
            //----------------------------------------------
            tileProviders: [
                {
                    id: "t01",
                    name: "標準地図",
                    visible: true,
                    attribution: '<a target="_blank" href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
                    url: "https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png",
                    options: {},
                },
                {
                    id: "t01a",
                    name: "淡色地図",
                    visible: false,
                    attribution: '<a target="_blank" href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
                    url: "https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png",
                    options: {},
                },
                {
                    id: "t02",
                    name: "衛星写真",
                    visible: false,
                    attribution: '<a target="_blank" href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
                    url: "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
                    options: {},
                },
                {
                    id: "t03",
                    name: "OpenStreet",
                    visible: false,
                    attribution: '<a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a>',
                    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                    options: {},
                },
                {
                    id: "t04",
                    name: "白地図",
                    visible: false,
                    attribution: '<a target="_blank" href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
                    url: "https://cyberjapandata.gsi.go.jp/xyz/blank/{z}/{x}/{y}.png",
                    options: {
                        maxNativeZoom: 14,
                        maxZoom: 18,
                    },
                },
                // {
                //     name: "GoogleMap",
                //     visible: false,
                //     url: "https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}",
                // },
                // {
                //     name: "GoogleMap",
                //     visible: false,
                //     url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                // }
            ],
            //----------------------------------------------
            nowCastList: [],
            nowCastProviders: [
                {
                    id: "nc001",
                    url: "https://www.jma.go.jp/bosai/jmatile/data/nowc/20240101235500/none/20240101235500/surf/hrpns/{z}/{x}/{y}.png",
                    visible: true,
                },
            ],
            nowCastVisible: false,
            nowCastListUrlN1: "https://www.jma.go.jp/bosai/jmatile/data/nowc/targetTimes_N1.json",
            nowCastListUrlN2: "https://www.jma.go.jp/bosai/jmatile/data/nowc/targetTimes_N2.json",
            sliderVal: 0,
            sliderMax: 0,
            nowCastOptions: {
                maxNativeZoom: 10,
                maxZoom: 18,
            },
            //----------------------------------------------
            //url: "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
            //url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            center: {
                lat: 36.8444607407,
                lng: 138.273925,
            },
            zoom: 5,
            nowZoom: 5,
            markers: [
                //{ lat: 34.2692895, lng: 136.5897455, name: "真珠ようしょく" },
                //{ lat: 34.2825895, lng: 136.776867, name: "定置あみ" }
            ],
            //----------------------------------------------
            windowWidth: window.innerWidth, // 画面サイズ
            windowHeight: window.innerHeight, //画面サイズ
            modalDevice: {
                id: 0,
                name: "",
                jpgImg: "",
                date: "",
            },
            aspect: "16:9",
            isLoading: false,
        };
    },
    //========================================================
    created: async function () {
        //---------------------------------
        this.jwt = this.$localStorage.get("jwt");
        if (!this.jwt) {
            this.$router.push({ name: "Login" });
            return;
        }
        this.myInfo = this.$jwt.decode(this.jwt);
        if (this.myInfo && this.myInfo.exp < this.$moment().unix()) {
            this.$router.push({ name: "Login" });
            return;
        }
        //---------------------------------
        if (this.$localStorage.get("map.zoom")) this.zoom = Number(this.$localStorage.get("map.zoom"));
        if (this.$localStorage.get("map.lat")) this.center.lat = Number(this.$localStorage.get("map.lat"));
        if (this.$localStorage.get("map.lng")) this.center.lng = Number(this.$localStorage.get("map.lng"));
        if (this.$localStorage.get("map.layer")) {
            let layerName = this.$localStorage.get("map.layer");
            for (let layerObj of this.tileProviders) {
                if (layerObj.name == layerName) layerObj.visible = true;
                else layerObj.visible = false;
            }
        }
        this.nowZoom = this.zoom;
        //---------------------------------
        this.isLoading = true;
        await this.getSites();
        await this.getDevices();
        await this.getLastSensings();
        await this.markerRegist();
        this.isLoading = false;
        //---------------------------------
        window.scrollTo(0, 0);
    },
    //========================================================
    watch: {},
    //========================================================
    mounted: async function () {
        window.addEventListener("resize", this.handleResize);
        //---------------------------------
        await this.getNowCastList();
        this.dispMap = true;
        //---------------------------------
    },
    //====================================================
    beforeDestroy: function () {
        window.removeEventListener("resize", this.handleResize);
    },
    //====================================================
    computed: {
        nowCastTime: function () {
            const idx = this.sliderVal;
            const obj = this.nowCastList[idx];
            const validtime = obj["validtime"];
            return this.$moment(validtime, "YYYYMMDDHHmmss").add(+9, "hour").format("HH:mm");
        },
    },
    //====================================================
    methods: {
        //====================================================
        baselayerChanged: function (layer) {
            //self.console.log(layer.name);
            this.$localStorage.set("map.layer", layer.name);
        },
        zoomUpdated: function (zoom) {
            //self.console.log(zoom);
            this.$localStorage.set("map.zoom", zoom);
            //this.zoom = zoom;
            this.nowZoom = zoom;
        },
        centerUpdated: function (center) {
            //self.console.log(center);
            this.$localStorage.set("map.lat", center.lat);
            this.$localStorage.set("map.lng", center.lng);
        },
        //====================================================
        mapCSS: function () {
            let h = this.windowHeight;
            h = h - 60;
            if (h < 300) h = 300;
            return "height: " + h + "px;";
        },
        //====================================================
        handleResize: function () {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
        },
        //====================================================
        async getNowCastList() {
            this.nowCastList = [];
            //--------------------------------------------------
            // 雨雲予測
            await this.axios({
                method: "GET",
                url: this.nowCastListUrlN2,
                timeout: 30000,
            }).then((response) => {
                this.nowCastList.push(...response.data);
            });
            //--------------------------------------------------
            // 雨雲実績
            await this.axios({
                method: "GET",
                url: this.nowCastListUrlN1,
                timeout: 30000,
            }).then((response) => {
                this.nowCastList.push(...response.data);
            });
            this.sliderMax = this.nowCastList.length - 1;
            //--------------------------------------------------
            // 並べ替え
            this.nowCastList.sort(function (a, b) {
                let ret = 0;
                if (a.validtime > b.validtime) ret = 1;
                if (a.validtime < b.validtime) ret = -1;
                if (a.validtime == b.validtime) ret = 0;
                return ret;
            });
            //--------------------------------------------------
            this.nowCastProviders = [];
            for (let idx in this.nowCastList) {
                const obj = this.nowCastList[idx];
                const basetime = obj["basetime"];
                const validtime = obj["validtime"];
                let visible = false;
                if (obj["basetime"] == obj["validtime"]) {
                    this.sliderVal = idx;
                    // visible = true;
                }
                this.nowCastProviders.push({
                    id: "nc" + String(idx),
                    url: "https://www.jma.go.jp/bosai/jmatile/data/nowc/" + basetime + "/none/" + validtime + "/surf/hrpns/{z}/{x}/{y}.png",
                    visible: visible,
                });
            }
            //--------------------------------------------------
            //self.console.log(this.nowCastProviders);
            //await new Promise((r) => setTimeout(r, 100));
        },
        //====================================================
        nowCastSW() {
            this.nowCastVisible = !this.nowCastVisible;
            this.nowCastChange();
        },
        //====================================================
        async geoSW() {
            this.geoJsonVisible = !this.geoJsonVisible;
            if (this.geoJsonVisible) {
                this.isLoading = true;
                await this.getGeoJson();
                this.isLoading = false;
            } else {
                this.isLoading = true;
                this.geojson = null;
                this.isLoading = false;
            }
        },
        //====================================================
        nowCastChange() {
            for (let idx in this.nowCastProviders) {
                if (idx == this.sliderVal && this.nowCastVisible == true) {
                    this.nowCastProviders[idx].visible = true;
                } else {
                    this.nowCastProviders[idx].visible = false;
                }
            }
        },
        //====================================================
        async getGeoJson() {
            await this.axios({
                method: "GET",
                // url: "/web/geojson",
                url: "/web/api/geojson",
                headers: { Authorization: "Bearer " + this.jwt },
                timeout: 1000 * 60 * 10,
            }).then((response) => {
                this.geojson = response.data;
            });
        },
        //====================================================
        async getSites() {
            await this.axios({
                method: "GET",
                url: "/web/api/sites",
                // params: { "_order[sort_no]": "desc" },
                headers: { Authorization: "Bearer " + this.jwt },
                timeout: 30000,
            }).then((response) => {
                this.dbSites = response.data.json;
            });
        },
        //====================================================
        async getDevices() {
            await this.axios({
                method: "GET",
                url: "/web/api/devices",
                // params: { is_active: 1, "_order[id]": "asc" },
                headers: { Authorization: "Bearer " + this.jwt },
                timeout: 30000,
            }).then((response) => {
                this.dbDevices = response.data.json;
            });
        },
        //====================================================
        async getLastSensings() {
            await this.axios({
                method: "GET",
                url: "/web/api/last_sensings",
                // params: { is_active: 1, "_order[id]": "asc" },
                headers: { Authorization: "Bearer " + this.jwt },
                timeout: 30000,
            }).then((response) => {
                this.dbLastSensings = response.data.json;
            });
        },
        //====================================================
        // async getRaspiConfig(mac) {
        //     let ret = null;
        //     await this.axios({
        //         method: "GET",
        //         url: "/web/api/raspi_configs",
        //         params: {
        //             mac: mac,
        //             _fields: "mac,float_low_interval,float_high_interval",
        //         },
        //         headers: { Authorization: "Bearer " + this.jwt },
        //         timeout: 30000,
        //     }).then((response) => {
        //         if (response.data.json[0]) ret = response.data.json[0];
        //     });
        //     //await new Promise((r) => setTimeout(r, 100));
        //     return ret;
        // },
        //====================================================
        floatStatus(device_id) {
            let ret = "0";
            for (let obj of this.dbLastSensings) {
                if (device_id == obj.device_id) {
                    ret = obj.bit_warning || obj.bit_fluct || obj.bit_float ? 1 : 0;
                }
            }
            return Number(ret);
        },
        //====================================================
        async markerRegist() {
            //------------------------------------------
            const grayIcon = L.icon({
                iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
                iconUrl: require("leaflet/dist/images/marker-icon.png"),
                shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
                iconSize: [25, 41],
                shadowSize: [41, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                tooltipAnchor: [16, -28],
                className: "marker-gray", // <= ここでクラス名を指定
            });
            const yellowIcon = L.icon({
                iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
                iconUrl: require("leaflet/dist/images/marker-icon.png"),
                shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
                iconSize: [25, 41],
                shadowSize: [41, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                tooltipAnchor: [16, -28],
                className: "marker-yellow", // <= ここでクラス名を指定
            });
            // const purpleIcon = L.icon({
            //     iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
            //     iconUrl: require("leaflet/dist/images/marker-icon.png"),
            //     shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
            //     iconSize: [25, 41],
            //     shadowSize: [41, 41],
            //     iconAnchor: [12, 41],
            //     popupAnchor: [1, -34],
            //     tooltipAnchor: [16, -28],
            //     className: "marker-purple", // <= ここでクラス名を指定
            // });
            //------------------------------------------
            this.legendMarker = grayIcon.options.iconUrl; //凡例用のマーカー画像 (色はclass名を定して変更する事)
            //------------------------------------------
            for (let idx in this.dbDevices) {
                let gps = this.dbDevices[idx].gps;
                let device_id = this.dbDevices[idx].id;
                let device_name = this.dbDevices[idx].name;
                let alived_at = this.dbDevices[idx].alived_at;
                let is_rest = this.dbDevices[idx].is_rest;
                let interval_long = this.dbDevices[idx].interval_long;
                let interval_short = this.dbDevices[idx].interval_short;
                let icon = null; //通常水位のマーカーは青
                let float_sw = this.floatStatus(device_id);
                //---------------------------------------------
                if (float_sw == "1") icon = self.L.spriteIcon("red"); //危険水位のマーカーは赤
                //if (this.$moment(alived_at) < this.$moment().add(-2, "hours")) icon = self.L.spriteIcon("yellow"); //2時間通信無しは黄色
                //---------------------------------------------
                if (alived_at != null) {
                    const now = this.$moment().format("YYYY-MM-DD HH:mm:ss");
                    let deadtime = this.$moment(alived_at).format("YYYY-MM-DD HH:mm:ss");
                    if (float_sw == "1") {
                        deadtime = this.$moment(alived_at)
                            .add(interval_short * 3, "minutes")
                            .format("YYYY-MM-DD HH:mm:ss");
                    } else {
                        deadtime = this.$moment(alived_at)
                            .add(interval_long * 3, "minutes")
                            .format("YYYY-MM-DD HH:mm:ss");
                    }
                    if (deadtime < now) {
                        icon = yellowIcon;
                        icon = grayIcon; // 広島要望により、通信異常はグレーアイコン (2025-09-09)
                    }
                } else {
                    icon = grayIcon;
                }
                //---------------------------------------------
                if (is_rest == 1) icon = grayIcon;
                //---------------------------------------------
                if (gps) {
                    let latlon = gps.split(",");
                    let lat = Number(latlon[0]);
                    let lng = Number(latlon[1]);
                    this.markers.push({
                        lat: lat,
                        lng: lng,
                        device_id: device_id,
                        device_name: device_name,
                        icon: icon,
                        device: JSON.parse(JSON.stringify(this.dbDevices[idx])),
                    });
                }
            }
        },
        //====================================================
    },
    //========================================================
};
</script>
